import { useState, useEffect } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Camera, HardDrive, Radio, FileText, Mail, TrendingUp, ArrowRight } from 'lucide-react';

const AdminDashboard = () => {
  const { API } = useGlobal();
  const { token } = useAuth();
  const [stats, setStats] = useState({
    cameraModules: 0,
    mdvrRecorders: 0,
    ultrasonicModels: 0,
    caseStudies: 0,
    contactSubmissions: 0
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [cameras, mdvr, ultrasonic, studies, submissions] = await Promise.all([
          axios.get(`${API}/camera-modules`),
          axios.get(`${API}/mdvr-recorders`),
          axios.get(`${API}/ultrasonic-models`),
          axios.get(`${API}/case-studies`),
          axios.get(`${API}/contact-submissions`, { headers })
        ]);

        setStats({
          cameraModules: cameras.data.length,
          mdvrRecorders: mdvr.data.length,
          ultrasonicModels: ultrasonic.data.length,
          caseStudies: studies.data.length,
          contactSubmissions: submissions.data.length
        });
        setRecentSubmissions(submissions.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, [API, token]);

  const statCards = [
    { icon: Camera, label: 'Camera Modules', value: stats.cameraModules, link: '/admin/camera-modules', color: 'bg-blue-100 text-blue-600' },
    { icon: HardDrive, label: 'MDVR & AI', value: stats.mdvrRecorders, link: '/admin/mdvr', color: 'bg-green-100 text-green-600' },
    { icon: Radio, label: 'Ultrasonic Models', value: stats.ultrasonicModels, link: '/admin/ultrasonic', color: 'bg-purple-100 text-purple-600' },
    { icon: FileText, label: 'Case Studies', value: stats.caseStudies, link: '/admin/case-studies', color: 'bg-orange-100 text-orange-600' },
    { icon: Mail, label: 'Contact Forms', value: stats.contactSubmissions, link: '/admin/submissions', color: 'bg-pink-100 text-pink-600' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 font-['Montserrat']">Dashboard</h1>
        <p className="text-slate-600">Welcome to Trinity Electronics CMS</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, idx) => (
          <Link 
            key={idx} 
            to={stat.link}
            className="stat-card hover:shadow-md transition-shadow"
          >
            <div className={`w-10 h-10 rounded-sm flex items-center justify-center mb-3 ${stat.color}`}>
              <stat.icon className="w-5 h-5" />
            </div>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 font-['Montserrat']">Quick Actions</h2>
          <div className="space-y-3">
            <Link 
              to="/admin/camera-modules?action=new"
              className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-sm transition-colors"
            >
              <span className="text-slate-700">Add Camera Module</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link 
              to="/admin/mdvr?action=new"
              className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-sm transition-colors"
            >
              <span className="text-slate-700">Add MDVR Model</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link 
              to="/admin/ultrasonic?action=new"
              className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-sm transition-colors"
            >
              <span className="text-slate-700">Add Ultrasonic Model</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link 
              to="/admin/settings"
              className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-sm transition-colors"
            >
              <span className="text-slate-700">Update Global Settings</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
          </div>
        </div>

        {/* Recent Submissions */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 font-['Montserrat']">
            Recent Contact Submissions
          </h2>
          {recentSubmissions.length > 0 ? (
            <div className="space-y-3">
              {recentSubmissions.map((sub) => (
                <div key={sub.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-sm">
                  <div>
                    <div className="font-medium text-slate-900">{sub.name}</div>
                    <div className="text-sm text-slate-500">{sub.company}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    sub.status === 'new' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No submissions yet</p>
          )}
          <Link 
            to="/admin/submissions"
            className="mt-4 inline-flex items-center text-[#5b3589] text-sm font-medium hover:underline"
          >
            View all submissions
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-purple-50 border border-purple-200 rounded-sm p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2 font-['Montserrat']">
          How to Use the CMS
        </h2>
        <p className="text-slate-600 text-sm mb-4">
          This CMS allows you to manage all content on the Trinity Electronics website without touching any code.
        </p>
        <ul className="text-slate-600 text-sm space-y-2">
          <li><strong>Camera Modules:</strong> Add/edit products for the Mobility Imaging page</li>
          <li><strong>MDVR & AI:</strong> Manage MDVR recorder models and specifications</li>
          <li><strong>Ultrasonic:</strong> Update animal deterrence product information</li>
          <li><strong>Hero Banners:</strong> Change hero images and text for each page</li>
          <li><strong>Global Settings:</strong> Update contact info, Teams URL, and e-commerce link</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboard;
